import { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { API } from "@/App";

const QuoteCartContext = createContext(null);

const STORAGE_KEY = "sterilemate_quote_cart_v1";
const FREIGHT_STORAGE_KEY = "sterilemate_quote_freight_v1";
const MICRO_TAT_STORAGE_KEY = "sterilemate_quote_micro_tat_v1";
const DEFAULT_FREIGHT_PCT = 0.20;

// Per-job fees applied ONCE when any microbiology lab test is in the cart.
// Sourced from the A&B Labs price sheet — applied AFTER the test subtotal.
const MICRO_LAB_FEES = [
  { key: "fedex",          label: "FedEx (flat rate per cooler)",            amount: 75.0 },
  { key: "environmental",  label: "Environmental Fee (per Job ID)",          amount: 55.0 },
  { key: "sustainability", label: "Sustainability Fee (supplies & disposal)", amount: 41.0 },
  { key: "delivery",       label: "Flat-rate sample delivery",                amount: 48.0 },
];
const MICRO_LAB_FEES_TOTAL = MICRO_LAB_FEES.reduce((s, f) => s + f.amount, 0); // $219

// Optional rush surcharge on the test subtotal — picked by the user in the
// Microbiology Testing catalog. Source: A&B Labs disclaimer.
const MICRO_TAT_TIERS = [
  { key: "standard", label: "Standard (5-7 BD)", pct: 0 },
  { key: "4bd",      label: "4 Business Days",   pct: 0.25 },
  { key: "3bd",      label: "3 Business Days",   pct: 0.50 },
  { key: "2bd",      label: "2 Business Days",   pct: 0.75 },
  { key: "1bd",      label: "1 Business Day (STAT)", pct: 1.00 },
];
const MICRO_MIN_INVOICE = 200.0;  // A&B Labs $200 minimum invoice fee.

export function QuoteCartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showCart, setShowCart] = useState(false);

  // Freight forwarder preference — persisted across the session
  const [freight, setFreight] = useState(() => {
    try {
      const raw = localStorage.getItem(FREIGHT_STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* noop */ }
    return { useOwn: false, carrier: "", account: "", contact: "" };
  });

  // Microbiology lab tests turnaround tier (rush surcharge applied to the
  // micro test subtotal — NOT to product line items).
  const [microTat, setMicroTat] = useState(() => {
    try {
      const raw = localStorage.getItem(MICRO_TAT_STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* noop */ }
    return "standard";
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cart)); } catch { /* noop */ }
  }, [cart]);
  useEffect(() => {
    try { localStorage.setItem(FREIGHT_STORAGE_KEY, JSON.stringify(freight)); } catch { /* noop */ }
  }, [freight]);
  useEffect(() => {
    try { localStorage.setItem(MICRO_TAT_STORAGE_KEY, JSON.stringify(microTat)); } catch { /* noop */ }
  }, [microTat]);

  const addItem = useCallback((item, qtyDelta = 1) => {
    // item: { erp, name, image, source?, category?, unit_price?, list_price?, pack? }
    const add = Math.max(1, parseInt(qtyDelta, 10) || 1);
    setCart(prev => {
      const exists = prev.find(i => i.erp === item.erp);
      if (exists) return prev.map(i => i.erp === item.erp ? { ...i, qty: i.qty + add } : i);
      return [...prev, { ...item, qty: add }];
    });
    toast.success(`${add} × ${item.name} added to quote cart`);
  }, []);

  const updateQty = useCallback((erp, delta) => {
    setCart(prev => prev.map(i => i.erp === erp ? { ...i, qty: Math.max(1, i.qty + delta) } : i));
  }, []);

  const setQty = useCallback((erp, qty) => {
    const n = Math.max(1, parseInt(qty || "1", 10) || 1);
    setCart(prev => prev.map(i => i.erp === erp ? { ...i, qty: n } : i));
  }, []);

  const removeItem = useCallback((erp) => {
    setCart(prev => prev.filter(i => i.erp !== erp));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  // ----- Derived totals ------------------------------------------------------
  const totals = useMemo(() => {
    // Split cart into microbiology lab tests vs. everything else, since lab
    // tests don't pay the 20% freight markup and instead trigger the A&B Labs
    // per-job flat fees + optional TAT rush surcharge + $200 min-invoice rule.
    const microItems = cart.filter(i => i.source === "microbiology");
    const otherItems = cart.filter(i => i.source !== "microbiology");

    const microRaw = microItems.reduce((s, i) => s + (Number(i.unit_price) || 0) * (i.qty || 0), 0);
    const otherSubtotal = otherItems.reduce((s, i) => s + (Number(i.unit_price) || 0) * (i.qty || 0), 0);

    const tatTier = MICRO_TAT_TIERS.find(t => t.key === microTat) || MICRO_TAT_TIERS[0];
    const microTatSurcharge = +(microRaw * tatTier.pct).toFixed(2);

    // Rolled lab-test subtotal (line items + rush surcharge)
    const microLineTotal = microRaw + microTatSurcharge;
    // $200 minimum invoice fee — applied if any micro tests are present and
    // the rolled lab-test subtotal falls below it. Top-up is tracked as its
    // own line so it shows transparently in the cart.
    const microMinTopUp = microItems.length > 0 && microLineTotal < MICRO_MIN_INVOICE
      ? +(MICRO_MIN_INVOICE - microLineTotal).toFixed(2)
      : 0;
    const microFeesTotal = microItems.length > 0 ? MICRO_LAB_FEES_TOTAL : 0;

    const subtotal = +(microRaw + otherSubtotal).toFixed(2);  // line items only (NO fees, NO surcharges)
    const pricedLines = cart.filter(i => Number(i.unit_price) > 0).length;
    const allPriced = pricedLines === cart.length && cart.length > 0;

    // 20% Sterile Mate freight markup applies ONLY to non-micro product items
    // (lab tests have their own delivery fee in MICRO_LAB_FEES).
    const freightCost = freight.useOwn ? 0 : +(otherSubtotal * DEFAULT_FREIGHT_PCT).toFixed(2);

    const total = +(
      subtotal +
      freightCost +
      microFeesTotal +
      microTatSurcharge +
      microMinTopUp
    ).toFixed(2);

    return {
      subtotal,
      freightCost,
      total,
      pricedLines,
      allPriced,
      freightPct: DEFAULT_FREIGHT_PCT,
      // micro-specific telemetry for the cart drawer
      hasMicro: microItems.length > 0,
      hasOther: otherItems.length > 0,
      microRaw: +microRaw.toFixed(2),
      microTat: tatTier,
      microTatSurcharge,
      microFees: microItems.length > 0 ? MICRO_LAB_FEES : [],
      microFeesTotal,
      microMinTopUp,
      microMinInvoice: MICRO_MIN_INVOICE,
    };
  }, [cart, freight.useOwn, microTat]);

  const submit = useCallback(async (guest) => {
    if (!guest.name || !guest.email || !guest.hospital || !guest.phone) {
      toast.error("Please fill in name, facility, email, and phone");
      return { ok: false };
    }
    if (!guest.address1 || !guest.city || !guest.state || !guest.zip) {
      toast.error("Please provide a complete shipping address");
      return { ok: false };
    }
    if (!cart.length) {
      toast.error("Cart is empty");
      return { ok: false };
    }
    if (freight.useOwn && !freight.carrier.trim()) {
      toast.error("Please provide your freight forwarder name");
      return { ok: false };
    }
    if (!guest.poNumber || !guest.poNumber.trim()) {
      toast.error("Please provide a Purchase Order number to submit an order");
      return { ok: false };
    }
    const paymentTerms = guest.paymentTerms || "Net 30";

    const itemsText = cart.map(i => {
      const uom = i.uom
        || (typeof i.pack === "string" ? i.pack : i.pack?.display)
        || (i.is_solm ? "case" : "each");
      const price = Number(i.unit_price) > 0
        ? ` @ $${Number(i.unit_price).toFixed(2)}/${uom} = $${(Number(i.unit_price) * i.qty).toFixed(2)}`
        : "";
      return `${i.name} (SKU: ${i.erp}, UOM: ${uom})${i.source ? ` [${i.source}]` : ""} x${i.qty}${price}`;
    }).join("; ");
    const shipping = `${guest.address1}${guest.address2 ? ", " + guest.address2 : ""}, ${guest.city}, ${guest.state} ${guest.zip}`;

    let freightBlock = "";
    if (freight.useOwn) {
      freightBlock = `\nFREIGHT: Hospital-provided forwarder\n  Carrier: ${freight.carrier}${freight.account ? `\n  Account: ${freight.account}` : ""}${freight.contact ? `\n  Contact: ${freight.contact}` : ""}\n  (Sterile Mate freight charges removed)`;
    } else if (totals.subtotal > 0 && totals.freightCost > 0) {
      freightBlock = `\nFREIGHT: Sterile Mate handling (20%) = $${totals.freightCost.toFixed(2)}`;
    }

    // Microbiology lab-test breakdown — appears only when the cart contains
    // micro tests, so non-lab quotes stay unchanged.
    let microBlock = "";
    if (totals.hasMicro) {
      const feesText = totals.microFees.map(f => `  ${f.label}: $${f.amount.toFixed(2)}`).join("\n");
      microBlock =
        `\n\nMICROBIOLOGY LAB CHARGES (A&B Labs · ISO 17025):` +
        `\n  Turnaround: ${totals.microTat.label}` +
        (totals.microTatSurcharge > 0
          ? `\n  TAT rush surcharge (+${(totals.microTat.pct * 100).toFixed(0)}%): $${totals.microTatSurcharge.toFixed(2)}`
          : "") +
        `\n${feesText}` +
        (totals.microMinTopUp > 0
          ? `\n  $200 minimum-invoice top-up: $${totals.microMinTopUp.toFixed(2)}`
          : "");
    }

    const totalsBlock = totals.subtotal > 0
      ? `\nSubtotal: $${totals.subtotal.toFixed(2)}${freight.useOwn ? "\nFreight:  hospital-provided" : (totals.freightCost > 0 ? `\nFreight:  $${totals.freightCost.toFixed(2)} (20%)` : "")}\nTOTAL:    $${totals.total.toFixed(2)}`
      : "";

    const notesBlock =
      `${cart.length} items: ${itemsText}\n` +
      `Ship To: ${shipping}` +
      `${freightBlock}${microBlock}${totalsBlock}` +
      `${guest.notes ? `\n\nCustomer Notes:\n${guest.notes}` : ""}`;

    try {
      const res = await fetch(`${API}/order-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order_type: "backorder",
          hospital_name: guest.hospital,
          address: guest.address1,
          contact_name: guest.name,
          contact_email: guest.email,
          contact_phone: guest.phone,
          item_name: itemsText,
          quantity: cart.reduce((s, i) => s + i.qty, 0),
          shipping_address: shipping,
          po_number: guest.poNumber.trim(),
          terms: paymentTerms,
          use_hospital_courier: !!freight.useOwn,
          hospital_courier_account: freight.useOwn
            ? `${freight.carrier}${freight.account ? ` (Acct ${freight.account})` : ""}${freight.contact ? ` · ${freight.contact}` : ""}`
            : "",
          notes:
            `Sterile Mate Order — PO ${guest.poNumber.trim()} · ${paymentTerms}\n` +
            notesBlock,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || `HTTP ${res.status}`);
      toast.success(body.message || "Order submitted!");
      return { ok: true, response: body };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Order submission failed", err);
      toast.error("Submission failed — please call 281.456.3458");
      return { ok: false };
    }
  }, [cart, freight, totals]);

  return (
    <QuoteCartContext.Provider value={{
      cart, addItem, updateQty, setQty, removeItem, clearCart,
      showCart, setShowCart, submit,
      freight, setFreight, totals,
      microTat, setMicroTat, MICRO_TAT_TIERS, MICRO_LAB_FEES,
    }}>
      {children}
    </QuoteCartContext.Provider>
  );
}

export function useQuoteCart() {
  const ctx = useContext(QuoteCartContext);
  if (!ctx) throw new Error("useQuoteCart must be used within QuoteCartProvider");
  return ctx;
}
